import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useCurrency, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import redeemIcon from '~Assets/icons/redeem.svg';
import { useExpressOrder } from '../../../Providers/ExpressOrderProvider';
import CodeInput from '../../../Components/CodeInput';
import Modal, { MODAL_TRANSITION_MS } from '../../../Components/Modal';

/** Same card as PromoCodeModal: keyframe in, CSSTransition-driven out. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  padding: 48px;
  border-radius: 36px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposGiftCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-gift-card-exit-active &,
  .epos-gift-card-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposGiftCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.6px;
`;

interface Props {
  open: boolean;
  onClose: () => void;
  /** The answer from the receipt step, recorded on the order when the card pays. */
  wantsReceipt: boolean;
}

/**
 * Enter a gift card code; a card that covers the whole order starts paying
 * straight away and checkout moves on to the payment step's loading state. Checking and charging live in
 * ExpressOrderProvider - this only asks and shows why a card was turned down.
 */
const GiftCardModal: FC<Props> = ({ open, onClose, wantsReceipt }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-gift-card');
  const currency = useCurrency();
  const expressOrder = useExpressOrder();

  const [error, setError] = useState('');
  /** CodeInput has no busy state - a second submit during the lookup is dropped here. */
  const submittingRef = useRef(false);

  /** Closed from anywhere (incl. the idle timeout) - the next open starts without an error. */
  useEffect(() => {
    if (!open) {
      setError('');
    }
  }, [open]);

  /** Typing again hides the error - a no-op render-wise when there is none. */
  const clearError = useCallback(() => setError(''), []);

  const handleSubmit = useCallback(
    async (code: string) => {
      /** Printed cards read "AB1-23C-D45" - the dashes and spaces are not part of the code. */
      const shortcode = code.replace(/[\s-]/g, '');
      if (!shortcode || submittingRef.current) {
        return;
      }
      submittingRef.current = true;
      logger.info('Gift card code submitted', { shortcode });

      try {
        const result = await expressOrder.payWithGiftCard(shortcode, wantsReceipt);
        /** Paying - checkout moves on and unmounts this step, so nothing to close. */
        if (result.success) {
          return;
        }
        logger.info('Gift card rejected', { shortcode, reason: result.reason });
        setError(
          i18n(`Express.GiftCard.Error.${result.reason}`, currency.format(result.balance ?? 0)),
        );
      } finally {
        submittingRef.current = false;
      }
    },
    [expressOrder.payWithGiftCard, wantsReceipt, onClose, currency],
  );

  return (
    <Modal
      open={open}
      exitPrefix="epos-gift-card"
      onBackdropTap={onClose}
      testId="epos-gift-card-modal">
      <Card>
        <Header>
          <Icon src={redeemIcon} color="var(--eposText)" width="36px" height="36px" />
          <Title>{i18n('Express.GiftCard.Title')}</Title>
        </Header>
        <CodeInput
          placeholder={i18n('Express.GiftCard.Placeholder')}
          submitLabel={i18n('Express.GiftCard.Pay')}
          onSubmit={handleSubmit}
          error={error}
          onChange={clearError}
        />
      </Card>
    </Modal>
  );
};

export default memo(GiftCardModal);
